/**
 * STH-57 — Black Friday campaign drops.
 *
 * Why this exists in JS at all:
 * Shopify serves storefront pages from its own full-page cache (responses carry
 * `etag: W/"page_cache:…:PageDetailsController:…"`). Any server-rendered decision about whether a
 * drop has landed can therefore be served stale — a customer could see a tile locked minutes after
 * it went live. Visual state is computed here from `data-release-at`, against the viewer's
 * clock. Liquid chooses teaser/product imagery using server time; released tiles refresh
 * that content so an open page or cached teaser can pick up a newly published product.
 *
 * States: upcoming -> live -> past, with sold-out as an overlay on a released drop.
 * "Live" is the single most-recently-released drop; earlier released drops are "past" so they
 * don't compete with today's (ticket requirement).
 */
if (!customElements.get('section-campaign-drops')) {
  const STATE = {
    UPCOMING: 'is-upcoming',
    LIVE: 'is-live',
    PAST: 'is-past',
    SOLD_OUT: 'is-sold-out',
  };

  const ALL_STATES = Object.values(STATE);

  const MINUTE = 60 * 1000;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  class TG_SectionCampaignDrops extends HTMLElement {
    constructor() {
      super();
      this.timerId = null;
      this.refreshTimer = null;
      this.lastAnnouncedAt = 0;
      this.lastAnnounceKey = null;
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.statusNode = this.querySelector('[data-campaign-status]');

      this.labels = {
        upcoming: this.dataset.upcomingLabel || 'Dropping in',
        live: this.dataset.liveLabel || 'Available now',
        past: this.dataset.pastLabel || 'Still available',
        soldOut: this.dataset.soldoutLabel || 'Sold out',
        mystery: this.dataset.mysteryLabel || 'Mystery drop',
        locked: this.dataset.lockedLabel || 'Locked',
        view: this.dataset.viewLabel || 'View',
      };

      this.tiles = Array.from(this.querySelectorAll('[data-campaign-drop]'))
        .map((el) => this.readTile(el))
        .filter((tile) => tile !== null);

      if (this.tiles.length === 0) return;

      this.tick();
      this.start();
      this.setupCursorChip();
      this.setupScrollFocus();
      this.setupHeadingCue();
      this.setupPromoVideoPause();

      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.teardownCursorChip();
      this.teardownScrollFocus();
      this.teardownHeadingCue();
      if (this.promoObserver) {
        this.promoObserver.disconnect();
        this.promoObserver = null;
      }
    }

    /**
     * Promo videos load lazily (no data-tg-video-auto — a grid full of eager videos is
     * wasted download) on first approach, then pause off-screen and resume on return.
     * prefers-reduced-motion users keep the poster: the video is never loaded at all.
     */
    setupPromoVideoPause() {
      this.promoObserver = null;
      const promos = Array.from(this.querySelectorAll('.CampaignDrop--promo'));
      if (promos.length === 0 || !('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.promoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('deferred-media:not([loaded])').forEach((media) => {
              if (media.loadContent && !media.dataset.autoplayBlocked) media.loadContent(false);
            });
          }
          entry.target.querySelectorAll('video').forEach((video) => {
            if (entry.isIntersecting) {
              const playing = video.play();
              if (playing && playing.catch) {
                playing.catch((error) => {
                  // Autoplay refused (iOS Low Power Mode etc.): put the merchant's cover
                  // image back instead of leaving the video's first frame over it. The
                  // flag stops loadContent above from re-downloading and re-failing on
                  // every pass. AbortError (pause() racing a pending play()) is routine
                  // scroll churn — leave the video alone for that.
                  if (!error || error.name !== 'NotAllowedError') return;
                  const media = video.closest('deferred-media');
                  if (media) {
                    media.dataset.autoplayBlocked = 'true';
                    media.removeAttribute('loaded');
                  }
                  video.remove();
                });
              }
            } else {
              video.pause();
            }
          });
        });
      }, { rootMargin: '160px 0px' });

      promos.forEach((el) => this.promoObserver.observe(el));
    }

    /**
     * Places the scroll-cue arrow one word-space after the heading's first line
     * ("19 HOODIES IN ↓"). The wrap point is content- and viewport-dependent, so it is
     * measured: a Range over the hero layer's text yields the first line box's rect.
     * Re-measured on resize (debounced) and once fonts finish loading.
     */
    setupHeadingCue() {
      this.onCueResize = null;
      this.cueResizeTimer = null;

      const root = this.closest('.CampaignDrops');
      const layer = root && root.querySelector('.CampaignDrops__HeadingLayer--hero');
      const cue = root && root.querySelector('[data-campaign-cue]');
      if (!layer || !cue || !layer.firstChild || layer.firstChild.nodeType !== Node.TEXT_NODE) return;

      const place = () => {
        const range = document.createRange();
        range.selectNodeContents(layer.firstChild);
        const rects = range.getClientRects();
        const heading = cue.offsetParent;
        if (!rects.length || !heading) return;

        const line = rects[0];
        const headingRect = heading.getBoundingClientRect();
        const fontSize = parseFloat(window.getComputedStyle(layer).fontSize) || 0;

        // One word-space (~0.24em in Oswald) after the line's last glyph; the stem hangs
        // from the cap band's midline.
        cue.style.setProperty('--cue-left', `${line.right - headingRect.left + fontSize * 0.24}px`);
        cue.style.setProperty('--cue-top', `${line.top - headingRect.top + line.height * 0.34}px`);
        cue.classList.add('is-placed');

        // Seam split must land on a line boundary or a line gets sliced mid-glyph.
        // Two lines -> 50% (the CSS default); three -> 66.7% (two over the hero); etc.
        const lineCount = rects.length;
        const above = Math.max(1, Math.round(lineCount / 2));
        heading.style.setProperty('--seam-split', `${((above / lineCount) * 100).toFixed(3)}%`);
      };

      place();
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => place()).catch(() => {});
      }

      this.onCueResize = () => {
        window.clearTimeout(this.cueResizeTimer);
        this.cueResizeTimer = window.setTimeout(place, 150);
      };
      window.addEventListener('resize', this.onCueResize);
    }

    teardownHeadingCue() {
      if (this.onCueResize) {
        window.removeEventListener('resize', this.onCueResize);
        this.onCueResize = null;
      }
      window.clearTimeout(this.cueResizeTimer);
    }

    /**
     * Parse one tile's markup into the shape the loop needs. A tile with an unparseable
     * release time is dropped rather than rendered in a wrong state — failing visibly in
     * the console beats silently showing a locked tile forever.
     */
    readTile(el) {
      const raw = el.dataset.releaseAt;
      const releaseAt = raw ? Date.parse(raw) : NaN;

      if (Number.isNaN(releaseAt)) {
        console.warn('[campaign-drops] skipping tile with unparseable data-release-at:', raw, el);
        return null;
      }

      return {
        el,
        releaseAt,
        isMystery: el.dataset.tileType === 'mystery',
        soldOut: el.dataset.soldOut === 'true',
        liveUrl: el.dataset.liveUrl || '',
        productImage: el.dataset.imageSource === 'product',
        renderedAt: Number(el.dataset.renderedAt) || 0,
        holdingUrl: el.dataset.holdingUrl || '',
        link: el.querySelector('[data-campaign-drop-link]'),
        labelNode: el.querySelector('[data-campaign-drop-label]'),
        countdownNode: el.querySelector('[data-campaign-drop-countdown]'),
        number: (el.querySelector('.CampaignDrop__Number') || {}).textContent || '',
        state: null,
        rect: null,
        panRafId: null,
      };
    }

    start() {
      if (this.timerId !== null || document.visibilityState === 'hidden') return;
      this.timerId = window.setInterval(() => this.tick(), 1000);
    }

    stop() {
      window.clearInterval(this.timerId);
      this.timerId = null;
      window.clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    /** Keep retrying delayed publication while visible, with one request for the grid. */
    scheduleRefresh(delay = 1500) {
      if (!this.isConnected || document.visibilityState === 'hidden' ||
          this.refreshTimer !== null || this.refreshInFlight) return;
      if (!this.tiles.some((tile) => tile.releaseAt <= Date.now() &&
          (!tile.liveUrl || !tile.productImage))) return;

      this.refreshTimer = window.setTimeout(async () => {
        this.refreshTimer = null;
        if (!this.isConnected || document.visibilityState === 'hidden') return;
        await this.refreshFromServer();
        this.scheduleRefresh(30 * 1000);
      }, delay);
    }

    /**
     * A backgrounded tab gets its timers throttled, so on return the countdown could be well
     * behind. Recompute immediately on show rather than waiting for the next interval — this
     * is also the path that makes a tile flip correctly for someone who left the page open
     * across a drop boundary (AC 3).
     */
    onVisibilityChange() {
      if (document.visibilityState === 'hidden') {
        this.hiddenAt = Date.now();
        this.stop();
      } else {
        // A tab hidden across a long window may have stale sold-out flags (and a
        // mystery drop may have released) — re-read the server's view of the tiles.
        if (this.hiddenAt && Date.now() - this.hiddenAt > 10 * MINUTE) {
          this.refreshFromServer();
        }
        this.tick();
        this.start();
      }
    }

    /**
     * Refresh released images, links and sold-out flags without replacing tiles or
     * countdown nodes. Fetch only this section, retaining the page's locale/preview.
     * Publishing and caching can lag release — retry while content is missing.
     */
    refreshFromServer() {
      if (this.refreshInFlight) return this.refreshInFlight;
      const url = new URL(window.location.href);
      const sectionId = this.closest('[data-section-id]')?.dataset.sectionId;
      if (sectionId) url.searchParams.set('section_id', sectionId);

      this.refreshInFlight = window
        .fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store' })
        .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          if (!this.isConnected) return;
          const fresh = new Map(Array.from(doc.querySelectorAll('[data-campaign-drop]'))
            .map((el) => [el.dataset.dropId, el]));

          this.tiles.forEach((tile) => {
            const el = fresh.get(tile.el.dataset.dropId);
            if (!el || tile.releaseAt > Date.now()) return;
            const renderedAt = Number(el.dataset.renderedAt) || 0;
            // Apply a newer, released server snapshot as a unit. An old teaser must
            // not erase a working link or sold-out flag while its photo stays live.
            // A genuinely newer unpublication can still remove the product link.
            if (el.dataset.releaseHidden !== 'false' || renderedAt <= tile.renderedAt) return;
            const liveUrl = el.dataset.liveUrl || '';
            const soldOut = el.dataset.soldOut === 'true';

            // The renderedAt gate above makes this snapshot strictly newer, so its media
            // is authoritative in BOTH directions: teaser -> product photo at release,
            // and product photo -> teaser when the drop's product was swapped for one
            // with no image (the old product's photo must not keep fronting the new link).
            const freshMedia = el.querySelector('.CampaignDrop__Image, .CampaignDrop__Placeholder');
            const currentMedia = tile.el.querySelector('.CampaignDrop__Image, .CampaignDrop__Placeholder');
            if (freshMedia && currentMedia) {
              if (freshMedia.outerHTML !== currentMedia.outerHTML) {
                currentMedia.replaceWith(freshMedia.cloneNode(true));
              }
              // Updated together with the swap: productImage describes what THIS DOM
              // shows, so it must only change when the media actually synced.
              tile.productImage = el.dataset.imageSource === 'product';
              tile.el.dataset.imageSource = el.dataset.imageSource;
            }

            tile.liveUrl = liveUrl;
            tile.soldOut = soldOut;
            tile.renderedAt = renderedAt;
            // Mirror every refreshed value back onto the DOM: readTile() re-parses these
            // attributes when the element reconnects (the theme editor re-mounts sections
            // on every tweak), and a stale dataset would resurrect the old link and
            // sold-out flag — while the already-bumped renderedAt blocks re-fetching them.
            tile.el.dataset.renderedAt = String(renderedAt);
            tile.el.dataset.soldOut = String(soldOut);
            if (liveUrl) {
              tile.el.dataset.liveUrl = liveUrl;
            } else {
              delete tile.el.dataset.liveUrl;
            }
            const headline = el.querySelector('.CampaignDrop__Headline');
            const currentHeadline = tile.el.querySelector('.CampaignDrop__Headline');
            if (liveUrl && headline && currentHeadline) currentHeadline.textContent = headline.textContent;
            if (tile.state) this.applyState(tile, tile.state);
          });
          this.tick();
        })
        .catch(() => {})
        .finally(() => {
          this.refreshInFlight = false;
        });
      return this.refreshInFlight;
    }

    tick() {
      const now = Date.now();

      // The live drop is the latest one whose release has passed. Computed per tick rather
      // than cached, so the handover from one drop to the next needs no special-casing.
      let liveIndex = -1;
      let liveReleaseAt = -Infinity;

      this.tiles.forEach((tile, index) => {
        if (tile.releaseAt <= now && tile.releaseAt > liveReleaseAt) {
          liveReleaseAt = tile.releaseAt;
          liveIndex = index;
        }
      });

      this.tiles.forEach((tile, index) => {
        const released = tile.releaseAt <= now;
        let state;

        if (!released) {
          state = STATE.UPCOMING;
        } else if (tile.soldOut) {
          state = STATE.SOLD_OUT;
        } else if (index === liveIndex) {
          state = STATE.LIVE;
        } else {
          state = STATE.PAST;
        }

        if (tile.state !== state) {
          this.applyState(tile, state);
          tile.state = state;
        }

        this.renderCountdown(tile, now);
      });

      this.announce(liveIndex, now);
      this.scheduleRefresh();
    }

    /**
     * State changes touch classes, the destination, and the tab order together — they are one
     * decision, not three, and splitting them is how a tile ends up focusable with no href.
     */
    applyState(tile, state) {
      // "Caught it live": a drop unlocking while its tile sits in the mobile focus band
      // gets a small star pulse — the reward for whoever parked on the countdown at :00.
      // (tile.state still holds the OLD state here; tick() assigns the new one after.)
      if (
        tile.state === STATE.UPCOMING &&
        state === STATE.LIVE &&
        tile.el.classList.contains('is-focused')
      ) {
        tile.el.classList.add('is-justUnlocked');
        tile.el.addEventListener(
          'animationend',
          () => tile.el.classList.remove('is-justUnlocked'),
          { once: true }
        );
      }

      tile.el.classList.remove(...ALL_STATES);
      tile.el.classList.add(state);

      const released = state !== STATE.UPCOMING;
      const href = released ? tile.liveUrl : tile.holdingUrl;

      if (tile.link) {
        if (href) {
          tile.link.setAttribute('href', href);
          tile.link.removeAttribute('tabindex');
          tile.link.removeAttribute('aria-hidden');
        } else {
          // Nowhere to go: an upcoming tile with no holding page must not be clickable or
          // reachable by keyboard (AC 6) — but it stays in the accessibility tree. The
          // headline and the VisuallyHidden release <time> are the only way a screen
          // reader can perceive an upcoming drop at all (sighted users get the countdown;
          // the per-second digits themselves stay aria-hidden).
          tile.link.removeAttribute('href');
          tile.link.setAttribute('tabindex', '-1');
          tile.link.removeAttribute('aria-hidden');
        }
      }

      if (tile.labelNode) {
        tile.labelNode.textContent = this.labelFor(tile, state);
      }

      if (state !== STATE.UPCOMING && tile.countdownNode) {
        tile.countdownNode.textContent = '';
      }

      // The shared cursor chip snapshots its label at mouseenter. A state flip under a
      // stationary cursor (a drop landing, or a pre-flagged sold-out releasing) would leave
      // that snapshot stale, so re-sync it whenever the hovered tile changes state.
      if (this.cursorChip && this.cursorTile === tile) {
        if (this.cursorChipEligible(state)) {
          this.cursorChipText.textContent = this.cursorChipLabel(state);
        } else {
          this.onCursorTileLeave(tile);
        }
      }
    }

    // The non-sold-out strings land in a hidden node, but they are still load-bearing:
    // the CSS :has(:not(:empty)) gate on the chip backing needs the label text present
    // on locked tiles (see the StatusInner comment in section-campaign-drops.css).
    labelFor(tile, state) {
      switch (state) {
        case STATE.SOLD_OUT:
          return this.labels.soldOut;
        case STATE.LIVE:
          return this.labels.live;
        case STATE.PAST:
          return this.labels.past;
        default:
          return tile.isMystery ? this.labels.mystery : this.labels.upcoming;
      }
    }

    renderCountdown(tile, now) {
      if (tile.state !== STATE.UPCOMING || !tile.countdownNode) return;

      const next = this.formatRemaining(tile.releaseAt - now);
      // Avoid touching the DOM when the string hasn't changed — with 22 tiles ticking every
      // second, most writes are redundant.
      if (tile.countdownNode.textContent !== next) {
        tile.countdownNode.textContent = next;
      }
    }

    /**
     * Coarse at a distance, precise close in. STH-55 owns the final format — the switch
     * points live here so changing them is a one-function edit, not a rewrite.
     */
    formatRemaining(ms) {
      if (ms <= 0) return '';

      const days = Math.floor(ms / DAY);
      const hours = Math.floor((ms % DAY) / HOUR);
      const minutes = Math.floor((ms % HOUR) / MINUTE);
      const seconds = Math.floor((ms % MINUTE) / 1000);

      if (days >= 1) {
        return days === 1 ? '1 day' : `${days} days`;
      }

      const pad = (n) => String(n).padStart(2, '0');
      return `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
    }

    /**
     * One polite announcement for the whole page, and only when the live drop actually
     * changes or a minute has passed. Announcing 22 per-second countdowns would make the
     * page unusable with a screen reader (AC 10).
     */
    announce(liveIndex, now) {
      if (!this.statusNode) return;

      const liveTile = liveIndex >= 0 ? this.tiles[liveIndex] : null;
      const liveNumber = liveTile ? liveTile.number : null;
      // The key carries the sold-out flag so the latest drop selling out re-announces
      // promptly instead of waiting out the minute cadence.
      const announceKey = liveTile ? `${liveNumber}:${liveTile.soldOut}` : null;

      const changed = announceKey !== this.lastAnnounceKey;
      if (!changed && now - this.lastAnnouncedAt < MINUTE) return;

      const upcoming = this.tiles.filter((tile) => tile.state === STATE.UPCOMING).length;

      let message;
      if (liveTile && liveTile.soldOut) {
        // liveIndex is purely release-time-based; when the latest drop is sold out no
        // tile is in the live state, and announcing it as live would be wrong.
        message = `Drop ${liveNumber} has sold out. ${upcoming} still to come.`;
      } else if (liveTile) {
        message = `Drop ${liveNumber} is live. ${upcoming} still to come.`;
      } else if (upcoming > 0) {
        message = `${upcoming} drops still to come.`;
      } else {
        message = 'All drops released.';
      }

      this.statusNode.textContent = message;
      this.lastAnnounceKey = announceKey;
      this.lastAnnouncedAt = now;
    }

    /**
     * Cursor-follow "Locked" / sold-out chip (skylrk mechanic). Desktop-hover only — a touch
     * device has no cursor to follow, and the centred status chip (see the CSS state hooks)
     * already communicates locked/sold-out there, so this is skipped entirely on touch.
     *
     * Listeners are bound once per tile here, at connectedCallback, and read `tile.state`
     * live at event time rather than being rebound when state changes — `tick()` keeps
     * `tile.state` current every second, so the handlers never see a stale value.
     */
    setupCursorChip() {
      this.cursorListeners = [];
      this.cursorRafId = null;
      this.cursorChip = null;
      this.cursorTile = null;

      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      // Reduced motion: the chip still appears (it is informative), but it snaps to the
      // cursor instead of chasing it — see the LERP choice in runCursorLoop.
      this.cursorSnap = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const chip = this.querySelector('.CampaignDrops__Cursor');
      if (!chip) return;

      this.cursorChip = chip;
      // Label text lives in a child span (the star glyph is a sibling); fall back to the
      // chip itself so older markup keeps working.
      this.cursorChipText = chip.querySelector('[data-campaign-cursor-text]') || chip;
      this.cursorTarget = { x: 0, y: 0 };
      this.cursorCurrent = { x: 0, y: 0 };
      this.cursorVisible = false;

      // Three delegated listeners for the whole grid (was three per tile, ~150 total
      // on a full campaign). Handlers resolve the tile from the event target and read
      // tile.state at event time, so nothing rebinds when states change.
      this.tileByEl = new Map(this.tiles.map((tile) => [tile.el, tile]));
      const tileFrom = (node) => {
        const el = node && node.closest && node.closest('[data-campaign-drop]');
        return (el && this.tileByEl.get(el)) || null;
      };

      const onOver = (event) => {
        const tile = tileFrom(event.target);
        if (!tile || tile === this.cursorTile) return;
        if (this.cursorTile) this.onCursorTileLeave(this.cursorTile);
        this.onCursorTileEnter(tile, event);
      };
      const onMove = (event) => {
        if (this.cursorTile) this.onCursorTileMove(this.cursorTile, event);
      };
      const onOut = (event) => {
        const from = tileFrom(event.target);
        if (!from || from !== this.cursorTile) return;
        if (tileFrom(event.relatedTarget) !== from) this.onCursorTileLeave(from);
      };

      this.addEventListener('mouseover', onOver);
      this.addEventListener('mousemove', onMove);
      this.addEventListener('mouseout', onOut);
      this.cursorListeners.push(
        { target: this, type: 'mouseover', handler: onOver },
        { target: this, type: 'mousemove', handler: onMove },
        { target: this, type: 'mouseout', handler: onOut }
      );
    }

    /**
     * Mobile scroll-focus — the touch-native stand-in for hover. Runs only where
     * setupCursorChip() doesn't. An IntersectionObserver marks the tile row crossing the
     * viewport's centre band with .is-focused (star mark + slow image drift, see CSS), and
     * a single delegated tap listener pulses truly-inert locked tiles so they feel alive.
     * No scroll listeners, no rAF, no new intervals.
     */
    setupScrollFocus() {
      this.focusObserver = null;
      this.onTileTap = null;

      if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      // ~10% central band. Pixel margins, not percentages — the IO spec's basis for
      // resolving percentage rootMargin is ambiguous across engines — recomputed on
      // resize/rotation since they freeze at observer creation.
      const buildFocusObserver = () => {
        if (this.focusObserver) this.focusObserver.disconnect();
        const band = Math.round(window.innerHeight * 0.45);
        this.focusObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle('is-focused', entry.isIntersecting);
          });
        }, { rootMargin: `-${band}px 0px -${band}px 0px`, threshold: 0 });
        this.tiles.forEach((tile) => this.focusObserver.observe(tile.el));
      };
      buildFocusObserver();

      this.onFocusResize = () => {
        window.clearTimeout(this.focusResizeTimer);
        this.focusResizeTimer = window.setTimeout(buildFocusObserver, 250);
      };
      window.addEventListener('resize', this.onFocusResize);

      this.onTileTap = (event) => {
        const el = event.target.closest('[data-campaign-drop]');
        const tile = el && this.tiles.find((t) => t.el === el);
        // Only tiles with genuinely nowhere to go — one with a holding_url already
        // navigates normally and needs no synthetic feedback.
        if (!tile || tile.state !== STATE.UPCOMING || tile.holdingUrl) return;

        el.classList.remove('is-tapped');
        void el.offsetWidth; // restart the keyframe if tapped again mid-animation
        el.classList.add('is-tapped');
      };
      this.addEventListener('click', this.onTileTap);
    }

    teardownScrollFocus() {
      if (this.focusObserver) {
        this.focusObserver.disconnect();
        this.focusObserver = null;
      }
      if (this.onFocusResize) {
        window.removeEventListener('resize', this.onFocusResize);
        this.onFocusResize = null;
      }
      window.clearTimeout(this.focusResizeTimer);
      if (this.onTileTap) {
        this.removeEventListener('click', this.onTileTap);
        this.onTileTap = null;
      }
    }

    teardownCursorChip() {
      (this.cursorListeners || []).forEach(({ target, type, handler }) => {
        target.removeEventListener(type, handler);
      });
      this.cursorListeners = [];

      if (this.cursorRafId !== null) {
        window.cancelAnimationFrame(this.cursorRafId);
        this.cursorRafId = null;
      }

      (this.tiles || []).forEach((tile) => {
        if (tile.panRafId !== null) {
          window.cancelAnimationFrame(tile.panRafId);
          tile.panRafId = null;
        }
      });
    }

    // Every stated tile gets the star: locked/sold-out keep their labels, unlocked
    // (live/past) tiles invite with the view label. Null = pre-first-tick, no chip.
    cursorChipEligible(state) {
      return state !== null;
    }

    cursorChipLabel(state) {
      switch (state) {
        case STATE.SOLD_OUT:
          return this.labels.soldOut;
        case STATE.LIVE:
        case STATE.PAST:
          return this.labels.view;
        default:
          return this.labels.locked;
      }
    }

    onCursorTileEnter(tile, event) {
      this.cursorTile = tile;
      // Cached for the hover pan — cursor-relative precision doesn't need a fresh
      // rect per mousemove, and reading layout in a move handler causes thrash.
      tile.rect = tile.el.getBoundingClientRect();

      if (!this.cursorChip || !this.cursorChipEligible(tile.state)) return;

      this.cursorChipText.textContent = this.cursorChipLabel(tile.state);
      // No offset here — the CSS margin places the star so its edge rests on the pointer.
      this.cursorTarget.x = event.clientX;
      this.cursorTarget.y = event.clientY;
      this.cursorCurrent.x = this.cursorTarget.x;
      this.cursorCurrent.y = this.cursorTarget.y;
      this.cursorChip.style.transform = `translate3d(${this.cursorCurrent.x}px, ${this.cursorCurrent.y}px, 0)`;
      this.cursorVisible = true;
      this.cursorChip.style.opacity = '1';
      this.runCursorLoop();
    }

    onCursorTileMove(tile, event) {
      this.updateHoverPan(tile, event);

      if (!this.cursorVisible) return;

      if (!this.cursorChipEligible(tile.state)) {
        this.onCursorTileLeave(tile);
        return;
      }

      this.cursorTarget.x = event.clientX;
      this.cursorTarget.y = event.clientY;
    }

    onCursorTileLeave(tile) {
      this.cursorTile = null;
      this.cursorVisible = false;
      if (this.cursorChip) this.cursorChip.style.opacity = '0';

      if (tile) {
        tile.el.style.setProperty('--drop-pan-x', '0px');
        tile.el.style.setProperty('--drop-pan-y', '0px');
      }
    }

    /**
     * Live-tile hover pan: the image drifts a few px toward the cursor, so hovering feels
     * like nudging the product around the tile rather than a fixed zoom. The CSS pairs the
     * translate with a slightly larger scale so no tile edge is ever exposed. Writes are
     * rAF-batched (one style write per frame per tile, mousemove fires far more often),
     * and the whole effect is skipped under prefers-reduced-motion.
     */
    updateHoverPan(tile, event) {
      // Every stated tile drifts — on locked tiles the silhouette shifts under the frost.
      if (this.cursorSnap || !tile.state || !tile.rect) return;

      const PAN_PX = 8;
      const nx = Math.max(-1, Math.min(1, ((event.clientX - tile.rect.left) / tile.rect.width) * 2 - 1));
      const ny = Math.max(-1, Math.min(1, ((event.clientY - tile.rect.top) / tile.rect.height) * 2 - 1));
      tile.panX = (nx * PAN_PX).toFixed(1);
      tile.panY = (ny * PAN_PX).toFixed(1);

      if (tile.panRafId !== null) return;
      tile.panRafId = window.requestAnimationFrame(() => {
        tile.panRafId = null;
        tile.el.style.setProperty('--drop-pan-x', `${tile.panX}px`);
        tile.el.style.setProperty('--drop-pan-y', `${tile.panY}px`);
      });
    }

    /**
     * rAF loop, lerping the chip 25% of the remaining distance toward the cursor each frame.
     * Keeps running while either the target hasn't been reached yet or the chip is still
     * visible (so it stays glued to a stationary cursor); stops only once both are true.
     */
    runCursorLoop() {
      if (this.cursorRafId !== null) return;

      const LERP = this.cursorSnap ? 1 : 0.25;
      const SETTLE_PX = 0.5;

      const step = () => {
        const dx = this.cursorTarget.x - this.cursorCurrent.x;
        const dy = this.cursorTarget.y - this.cursorCurrent.y;
        const settled = Math.abs(dx) < SETTLE_PX && Math.abs(dy) < SETTLE_PX;

        if (!settled) {
          this.cursorCurrent.x += dx * LERP;
          this.cursorCurrent.y += dy * LERP;
          if (this.cursorChip) {
            this.cursorChip.style.transform = `translate3d(${this.cursorCurrent.x}px, ${this.cursorCurrent.y}px, 0)`;
          }
        }

        if (settled && !this.cursorVisible) {
          this.cursorRafId = null;
          return;
        }

        this.cursorRafId = window.requestAnimationFrame(step);
      };

      this.cursorRafId = window.requestAnimationFrame(step);
    }
  }

  customElements.define('section-campaign-drops', TG_SectionCampaignDrops);
}
